import { supabase } from '@/lib/supabase'
import { TourInstance } from '@/types'
import { useQuery } from '@tanstack/react-query'
import {
  eachDayOfInterval,
  endOfMonth,
  format,
  isBefore,
  isSameDay,
  startOfDay,
  startOfMonth,
} from 'date-fns'
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Clock,
  MapPin,
  Users,
} from 'lucide-react'
import React, { useState } from 'react'

// Parse a 'YYYY-MM-DD' date string as a LOCAL calendar date.
//
// `new Date('YYYY-MM-DD')` parses the string as UTC midnight, which shifts the
// calendar day by one for users in some timezones when later compared against a
// local Date (e.g. isSameDay). Building the Date from its components keeps it on
// the intended calendar day regardless of the viewer's timezone. Non date-only
// strings (or anything unexpected) fall back to the native parser.
const parseLocalDate = (value: string | Date): Date => {
  if (value instanceof Date) return value
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (match) {
    const [, year, month, day] = match
    return new Date(Number(year), Number(month) - 1, Number(day))
  }
  return new Date(value)
}

// Get current date in Bangkok timezone (UTC+7)
// This ensures consistent cutoff regardless of user's local timezone
const getBangkokDate = () => {
  const now = new Date()
  const bangkokOffset = 7 * 60 // Bangkok is UTC+7
  const utcTime = now.getTime() + now.getTimezoneOffset() * 60000
  const bangkokTime = new Date(utcTime + bangkokOffset * 60000)
  return bangkokTime
}

// Get minimum bookable date in Bangkok timezone
// Paramotor allows same-day booking before 12:00 midday Bangkok time
const getMinBookableDate = (activitySlug?: string) => {
  const bangkokNow = getBangkokDate()
  const bangkokToday = new Date(
    bangkokNow.getFullYear(),
    bangkokNow.getMonth(),
    bangkokNow.getDate()
  )

  const isParamotor =
    activitySlug &&
    (activitySlug.includes('paramotor') || activitySlug === 'paramotor')
  if (isParamotor && bangkokNow.getHours() < 12) {
    return bangkokToday // Allow today
  }

  const tomorrow = new Date(bangkokToday)
  tomorrow.setDate(tomorrow.getDate() + 1)
  return tomorrow
}

interface TourAvailabilityCalendarProps {
  tourSlug?: string
  selectedTourId?: string | null
  onTourSelect: (tourInstance: TourInstance) => void
  className?: string
  mode?: 'scheduled' | 'custom' // New: support custom date selection
  onCustomDateSelect?: (date: Date) => void // New: callback for custom date selection
  activitySlug?: string // Activity slug for same-day booking rules
}

interface CalendarDay {
  date: Date
  isCurrentMonth: boolean
  tourInstances: TourInstance[]
}

const TourAvailabilityCalendar: React.FC<TourAvailabilityCalendarProps> = ({
  tourSlug,
  selectedTourId,
  onTourSelect,
  className = '',
  mode = 'scheduled',
  onCustomDateSelect,
  activitySlug,
}) => {
  const [currentDate, setCurrentDate] = useState(new Date())
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)

  // Fetch available tour instances for the current month (only in scheduled mode)
  const { data: tourInstances, isLoading } = useQuery({
    queryKey: [
      'tour-instances-calendar',
      format(currentDate, 'yyyy-MM'),
      tourSlug,
    ],
    enabled: mode === 'scheduled', // Only fetch when in scheduled mode
    queryFn: async () => {
      const startDate = format(startOfMonth(currentDate), 'yyyy-MM-dd')
      const endDate = format(endOfMonth(currentDate), 'yyyy-MM-dd')

      const { data, error } = await supabase
        .from('tour_instances')
        .select(
          `
          *,
          tour_schedule:tour_schedules(
            *,
            tour:tours(title, slug, level, duration_days)
          )
        `
        )
        .gte('date', startDate)
        .lte('date', endDate)
        .eq('status', 'scheduled')
        .order('date', { ascending: true })

      if (error) throw error

      // Filter out fully booked tours and apply tour slug filter
      let availableTours = data.filter(
        (instance) => instance.current_bookings < instance.max_capacity
      )

      if (tourSlug) {
        availableTours = availableTours.filter(
          (instance) => instance.tour_schedule?.tour?.slug === tourSlug
        )
      }

      // Group multi-day tours (only show Day 1)
      const groupedTours = availableTours.reduce(
        (groups: TourInstance[], instance: TourInstance) => {
          const isMultiDay = instance.notes?.includes(' of ') ?? false

          if (isMultiDay) {
            const match = instance.notes?.match(/Day (\d+) of (\d+)/)
            if (match) {
              const [, dayNum, totalDays] = match
              const dayNumber = parseInt(dayNum)

              // Only include Day 1 instances for multi-day tours
              if (dayNumber === 1) {
                groups.push({
                  ...instance,
                  isMultiDay: true,
                  totalDays: parseInt(totalDays),
                } as TourInstance & { isMultiDay: boolean; totalDays: number })
              }
            }
          } else {
            // Single day tour
            groups.push({
              ...instance,
              isMultiDay: false,
              totalDays: 1,
            } as TourInstance & { isMultiDay: boolean; totalDays: number })
          }

          return groups
        },
        []
      )

      return groupedTours
    },
  })

  // Generate calendar days with proper day-of-week alignment
  const generateCalendarDays = (): CalendarDay[] => {
    const start = startOfMonth(currentDate)
    const end = endOfMonth(currentDate)
    const days = eachDayOfInterval({ start, end })

    // Get the day of week the month starts on (0 = Sunday, 6 = Saturday)
    const startDayOfWeek = start.getDay()

    // Create padding days for the beginning of the month
    const paddingDays: CalendarDay[] = []
    for (let i = 0; i < startDayOfWeek; i++) {
      const paddingDate = new Date(start)
      paddingDate.setDate(paddingDate.getDate() - (startDayOfWeek - i))
      paddingDays.push({
        date: paddingDate,
        isCurrentMonth: false,
        tourInstances: [],
      })
    }

    // Create the actual month days
    const monthDays = days.map((date) => ({
      date,
      isCurrentMonth: true,
      tourInstances:
        tourInstances?.filter((instance: TourInstance) =>
          // parseLocalDate avoids the UTC-vs-local off-by-one day bug:
          // instance.date is a 'YYYY-MM-DD' string and `date` is a local Date.
          isSameDay(parseLocalDate(instance.date), date)
        ) || [],
    }))

    return [...paddingDays, ...monthDays]
  }

  const calendarDays = generateCalendarDays()

  const navigateMonth = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate)
    if (direction === 'prev') {
      newDate.setMonth(newDate.getMonth() - 1)
    } else {
      newDate.setMonth(newDate.getMonth() + 1)
    }
    setCurrentDate(newDate)
    setSelectedDate(null)
  }

  const handleDateClick = (day: CalendarDay) => {
    // In custom mode, allow selecting tomorrow and future dates (no same-day bookings)
    if (mode === 'custom') {
      const minBookableDate = getMinBookableDate(activitySlug)
      const dayStart = startOfDay(day.date)

      if (!isBefore(dayStart, minBookableDate)) {
        setSelectedDate(day.date)
        if (onCustomDateSelect) {
          onCustomDateSelect(day.date)
        }
      }
    }
    // In scheduled mode, only allow selecting dates with tours
    else if (day.tourInstances.length > 0) {
      setSelectedDate(day.date)
    }
  }

  const handleTourSelect = (tourInstance: TourInstance) => {
    onTourSelect(tourInstance)
    // parseLocalDate keeps the selected day on the same local calendar day used
    // by the grid, so the "Tours on {date}" lookup (isSameDay) matches.
    setSelectedDate(parseLocalDate(tourInstance.date))
  }

  const getDayClasses = (day: CalendarDay) => {
    const baseClasses =
      'h-8 w-8 sm:h-10 sm:w-10 md:h-12 md:w-12 flex items-center justify-center text-xs sm:text-sm font-medium rounded-lg transition-all duration-200 cursor-pointer relative'
    const minBookableDate = getMinBookableDate(activitySlug)
    const dayStart = startOfDay(day.date)

    let classes = baseClasses

    // Days not in current month (padding days)
    if (!day.isCurrentMonth) {
      classes += ' text-garage-text/20 cursor-default'
      return classes
    }

    // Past dates and today (no same-day bookings allowed)
    if (isBefore(dayStart, minBookableDate)) {
      classes += ' text-garage-text/30 cursor-not-allowed'
    }
    // Selected date
    else if (selectedDate && isSameDay(day.date, selectedDate)) {
      classes += ' bg-garage-accent text-garage-cream shadow-lg'
    }
    // Custom mode: all future dates are selectable
    else if (mode === 'custom') {
      classes +=
        ' bg-garage-card/50 text-garage-text hover:bg-garage-accent/20 hover:text-garage-dark border-2 border-garage-border/20 hover:border-garage-accent/50'
    }
    // Scheduled mode: Has tours available
    else if (day.tourInstances.length > 0) {
      classes +=
        ' bg-garage-primary/10 text-garage-primary hover:bg-garage-primary hover:text-garage-cream border-2 border-garage-primary/20 hover:border-garage-primary'
    }
    // Scheduled mode: No tours
    else {
      classes += ' text-garage-text/50 hover:bg-garage-card/50'
    }

    return classes
  }

  const getTourTypeColor = (level: string) => {
    switch (level) {
      case 'beginner':
        return 'bg-green-500'
      case 'intermediate':
        return 'bg-yellow-500'
      case 'expert':
        return 'bg-red-500'
      default:
        return 'bg-garage-accent'
    }
  }

  // Only show loading in scheduled mode (custom mode doesn't fetch data)
  if (isLoading && mode === 'scheduled') {
    return (
      <div
        className={`bg-garage-card border-2 border-garage-border rounded-lg p-3 sm:p-4 md:p-6 ${className}`}
      >
        <div className="flex items-center justify-center py-6 sm:py-8">
          <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-garage-accent"></div>
          <span className="ml-2 sm:ml-3 text-sm sm:text-base text-garage-text">
            Loading calendar...
          </span>
        </div>
      </div>
    )
  }

  return (
    <div
      className={`bg-garage-card border-2 border-garage-border rounded-lg p-3 sm:p-4 md:p-6 ${className}`}
    >
      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <h3 className="text-sm sm:text-base md:text-lg font-bold text-garage-dark font-garage-title flex items-center gap-2">
          <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-garage-accent" />
          <span className="hidden sm:inline">
            {mode === 'custom'
              ? 'Select Your Preferred Date'
              : tourSlug
              ? 'Available Dates'
              : 'Available Tours'}
          </span>
        </h3>

        <div className="flex items-center gap-1 sm:gap-2">
          <button
            onClick={() => navigateMonth('prev')}
            className="p-1 sm:p-2 hover:bg-garage-border/20 rounded-lg transition-colors"
          >
            <ChevronLeft className="h-4 w-4 text-garage-text" />
          </button>

          <span className="text-xs sm:text-sm md:text-base text-garage-dark font-semibold min-w-[90px] sm:min-w-[120px] text-center">
            {format(currentDate, 'MMM yyyy')}
          </span>

          <button
            onClick={() => navigateMonth('next')}
            className="p-1 sm:p-2 hover:bg-garage-border/20 rounded-lg transition-colors"
          >
            <ChevronRight className="h-4 w-4 text-garage-text" />
          </button>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-0.5 sm:gap-1 mb-3 sm:mb-4">
        {/* Day headers */}
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
          <div
            key={day}
            className="h-6 sm:h-8 flex items-center justify-center text-[10px] sm:text-xs font-semibold text-garage-text/70"
          >
            {day}
          </div>
        ))}

        {/* Calendar days */}
        {calendarDays.map((day) => (
          <div key={day.date.toISOString()} className="relative">
            <button
              className={getDayClasses(day)}
              onClick={() => handleDateClick(day)}
              type="button"
              disabled={
                !day.isCurrentMonth ||
                isBefore(
                  startOfDay(day.date),
                  getMinBookableDate(activitySlug)
                ) ||
                (mode === 'scheduled' && day.tourInstances.length === 0)
              }
            >
              {format(day.date, 'd')}

              {/* Tour indicators (only in scheduled mode) */}
              {mode === 'scheduled' && day.tourInstances.length > 0 && (
                <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 flex gap-1">
                  {day.tourInstances.slice(0, 3).map((instance) => (
                    <div
                      key={instance.id}
                      className={`w-1.5 h-1.5 rounded-full ${getTourTypeColor(
                        instance.tour_schedule?.tour?.level || 'beginner'
                      )}`}
                    />
                  ))}
                  {day.tourInstances.length > 3 && (
                    <div className="w-1.5 h-1.5 rounded-full bg-garage-text/50" />
                  )}
                </div>
              )}
            </button>
          </div>
        ))}
      </div>

      {/* Legend (only in scheduled mode) */}
      {mode === 'scheduled' && (
        <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-[10px] sm:text-xs text-garage-text/70 mb-3 sm:mb-4">
          <div className="flex items-center gap-1">
            <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-green-500"></div>
            <span>Beginner</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-yellow-500"></div>
            <span>Intermediate</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-red-500"></div>
            <span>Expert</span>
          </div>
        </div>
      )}

      {/* Selected Date Tours (only in scheduled mode) */}
      {mode === 'scheduled' && selectedDate && (
        <div className="border-t border-garage-border/30 pt-3 sm:pt-4">
          <h4 className="font-semibold text-sm sm:text-base text-garage-dark mb-2 sm:mb-3">
            Tours on {format(selectedDate, 'MMMM d, yyyy')}
          </h4>

          <div className="space-y-2 sm:space-y-3">
            {calendarDays
              .find((day) => selectedDate && isSameDay(day.date, selectedDate))
              ?.tourInstances.map((instance) => {
                const tour = instance.tour_schedule?.tour
                const availableSpots =
                  instance.max_capacity - instance.current_bookings
                const isSelected = selectedTourId === instance.id
                const isMultiDay = (instance as any).isMultiDay
                const totalDays = (instance as any).totalDays || 1

                return (
                  <button
                    key={instance.id}
                    onClick={() => handleTourSelect(instance)}
                    className={`w-full text-left p-3 sm:p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                      isSelected
                        ? 'border-garage-accent bg-garage-accent/10'
                        : 'border-garage-border/30 hover:border-garage-accent/50 hover:bg-garage-card/50'
                    }`}
                    type="button"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <h5 className="font-semibold text-sm sm:text-base text-garage-dark">
                          {tour?.title}
                          {isMultiDay && (
                            <span className="text-xs sm:text-sm text-garage-accent ml-2">
                              ({totalDays} days)
                            </span>
                          )}
                        </h5>

                        <div className="flex flex-wrap items-center gap-2 sm:gap-4 mt-2 text-xs sm:text-sm text-garage-text/80">
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            <span>{instance.start_time}</span>
                          </div>

                          <div className="flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            <span className="hidden sm:inline">
                              {availableSpots} spots available
                            </span>
                            <span className="sm:hidden">
                              {availableSpots} spots
                            </span>
                          </div>

                          <div className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            <span className="capitalize">{tour?.level}</span>
                          </div>
                        </div>
                      </div>

                      <div className="text-right flex-shrink-0">
                        <div className="text-base sm:text-lg font-bold text-garage-primary">
                          ${instance.price}
                        </div>
                        <div className="text-[10px] sm:text-xs text-garage-text/60">
                          per person
                        </div>
                      </div>
                    </div>
                  </button>
                )
              })}
          </div>
        </div>
      )}

      {/* Selected Date in Custom Mode */}
      {mode === 'custom' && selectedDate && (
        <div className="border-t border-garage-border/30 pt-3 sm:pt-4">
          <div className="p-3 sm:p-4 bg-garage-accent/10 border-2 border-garage-accent/30 rounded-lg">
            <h4 className="font-semibold text-sm sm:text-base text-garage-dark mb-1">
              Selected Date:
            </h4>
            <p className="text-garage-text font-medium">
              {format(selectedDate, 'MMMM d, yyyy')}
            </p>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!selectedDate &&
        mode === 'scheduled' &&
        tourInstances &&
        tourInstances.length === 0 && (
          <div className="text-center py-6 sm:py-8 text-garage-text/70">
            <Calendar className="h-6 w-6 sm:h-8 sm:w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm sm:text-base">
              {tourSlug
                ? `No scheduled dates available for this tour in ${format(
                    currentDate,
                    'MMMM yyyy'
                  )}.`
                : `No scheduled tours available in ${format(
                    currentDate,
                    'MMMM yyyy'
                  )}.`}
            </p>
            <p className="text-xs sm:text-sm mt-1">
              Try selecting a different month or suggest custom dates.
            </p>
          </div>
        )}
    </div>
  )
}

export default TourAvailabilityCalendar
